import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, CheckCircle, Clock, ChevronDown, Video, FileText, Unlock, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';

// Program content - 16 lessons (non-sequential, users can access any)
const programLessons = [
  {
    id: 1,
    title: 'Introduction',
    subtitle: 'Multifidus & QL Muscle Timing',
    description: '',
    videoUrl: '',
    category: 'intro',
    hasRichContent: true,
  },
  {
    id: 2,
    title: 'How to Use This Resource',
    subtitle: 'Getting Started Safely',
    description: '',
    videoUrl: '',
    category: 'intro',
    hasRichContent: true,
  },
  {
    id: 3,
    title: 'Your Back Story',
    subtitle: 'Creating a New Narrative',
    description: 'Integrate everything and develop your personal back wellness plan.',
    videoUrl: 'https://www.youtube.com/embed/jPDnIQFly-c',
    category: 'intro',
  },
  {
    id: 4,
    title: 'The Tests',
    subtitle: 'Multifidus & QL Assessment',
    description: '',
    videoUrl: '',
    category: 'intro',
    hasRichContent: true,
  },
  {
    id: 5,
    title: 'Remedy 1: The Test is the Remedy',
    subtitle: 'Testing itself acts as reset',
    description: 'The assessment process itself begins to retune the muscles. By simply performing the tests with awareness, you are already initiating change.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 6,
    title: 'Remedy 2: The Mirroring Effect',
    subtitle: 'Nervous system balance via observation',
    description: 'Fine-tuning the nervous system through observation and mirroring. A subtle but powerful technique for restoring bilateral balance.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 7,
    title: 'Remedy 3: Hanna 3-Part Flexion',
    subtitle: 'Multifidus timing, side-lying flexion',
    description: 'Connecting legs to pelvis / spine, head to pelvis / spine, and head to legs through the pelvis and spine. Also considered multifidus conditioning and reconditioning — part of our deep postural system.',
    videoUrl: 'https://www.youtube.com/embed/ePjCDq03Zqg',
    category: 'remedies',
  },
  {
    id: 8,
    title: 'Remedy 4: TVA Reset',
    subtitle: 'Reawakening the TVA',
    description: 'Reawakening the Transverse Abdominis — one of the key inner unit muscles that supports spinal stability.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 9,
    title: 'Remedy 5: Body Map / Midline Meditation',
    subtitle: 'Awareness + mapping midline',
    description: 'Building awareness of your body\'s midline through guided meditation and somatic mapping.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 10,
    title: 'Remedy 6: Torso Vector Breathing',
    subtitle: 'Breath to core stability',
    description: 'Using breath directionality to reconnect the core stabilizing system and restore torso integration.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 11,
    title: 'Remedy 7: Diagonals',
    subtitle: 'Cross-body activation',
    description: 'Activating the body\'s diagonal fascial lines to restore cross-body coordination and stability.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 12,
    title: 'Remedy 8: Guitar Strings',
    subtitle: 'Elastic fascial lines',
    description: 'Working with the body\'s elastic fascial lines — like tuning guitar strings to restore tone and responsiveness.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 13,
    title: 'Remedy 9: Body Scan + Colours',
    subtitle: 'Somatic + visualization',
    description: 'A somatic body scan combined with colour visualization to deepen body awareness and release holding patterns.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 14,
    title: 'Remedy 10: Diaphragm Reset',
    subtitle: 'Retiming the diaphragm',
    description: 'Restoring proper diaphragm timing and function — a key player in spinal stability and nervous system regulation.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 15,
    title: 'Remedy 11: Eyes to Toe',
    subtitle: 'Eye-tracking to integrate full chain',
    description: 'Using eye movement patterns to integrate the full kinetic chain from head to toe.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 16,
    title: 'Remedy 12: Free Motion vs Stability',
    subtitle: 'Leg drop, pelvis stability',
    description: 'Exploring the relationship between free leg movement and pelvic stability — learning when the leg takes the pelvis and when the pelvis stays.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 17,
    title: 'Remedy 13: Circles in the Body',
    subtitle: 'Spiral / rotation patterning',
    description: 'Working with spiral and rotational movement patterns to restore natural movement flow throughout the body.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 18,
    title: 'Remedy 14: Diagonals (Extended)',
    subtitle: 'Expanded cross-body activation',
    description: 'An expanded version of the Diagonals practice with deeper cross-body integration and coordination.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 19,
    title: 'Remedy 15: The Fish',
    subtitle: 'Spinal wave + lengthening',
    description: 'A flowing spinal wave practice that encourages lengthening and natural sequencing through the entire spine.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 20,
    title: 'Remedy 16: Waking Up the Pelvis',
    subtitle: 'Pelvis Clock — mobility + sequencing',
    description: 'Restoring pelvic mobility and sequencing using the Pelvis Clock — a foundational practice for lower body integration.',
    videoUrl: '',
    category: 'remedies',
  },
  {
    id: 21,
    title: 'Remedy 17: The Spine in 3 Planes',
    subtitle: 'Exploring the multi-talented spine',
    description: 'Exploring the spine\'s full range through all three planes of movement — flexion/extension, side-bending, and rotation.',
    videoUrl: '',
    category: 'remedies',
  },
];

const BackResetProgramPage = () => {
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [expandedSection, setExpandedSection] = useState('all');
  const [completedLessons, setCompletedLessons] = useState([]);
  const { user } = useAuth();
  const { trackView, trackVideoPlay, trackLinkClick, trackComplete, trackSectionClick } = useActivityTracker('back-reset');

  const hasRemedyAccess = user?.unlocked_programs?.includes('back-reset') || user?.is_admin;

  const handleLessonClick = (lesson) => {
    if (lesson.category === 'remedies' && !hasRemedyAccess) {
      setSelectedLesson({ ...lesson, locked: true });
    } else {
      setSelectedLesson(lesson);
      trackView(lesson.id, lesson.title + (lesson.subtitle ? ` - ${lesson.subtitle}` : ''));
    }
  };

  // Group lessons by category
  const sections = [
    { id: 'intro', title: 'Getting Started', lessons: programLessons.filter(l => l.category === 'intro') },
    { id: 'remedies-1', title: 'Remedies 1-9', lessons: programLessons.filter(l => l.category === 'remedies').slice(0, 9) },
    { id: 'remedies-2', title: 'Remedies 10-17', lessons: programLessons.filter(l => l.category === 'remedies').slice(9) },
  ];

  const toggleComplete = (lessonId) => {
    const isCompleting = !completedLessons.includes(lessonId);
    setCompletedLessons(prev => 
      prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
    if (isCompleting) {
      const lesson = programLessons.find(l => l.id === lessonId);
      trackComplete(lessonId, lesson?.title || `Lesson ${lessonId}`);
    }
  };

  const completedCount = completedLessons.length;
  const progressPercent = (completedCount / programLessons.length) * 100;

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="back-reset-program">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-64 md:h-80 relative overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_greeting-hub-217/artifacts/rzz31rvw_R%20%282%29.jpg"
            alt="The Back Reset Program"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8 w-full">
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 text-cream/70 hover:text-cream transition-colors mb-4"
              >
                <ArrowLeft size={18} />
                <span className="font-body text-sm">Back to Programs</span>
              </Link>
              <h1 className="font-heading text-4xl md:text-5xl text-white mb-2">The Back Reset Program</h1>
              <p className="text-cream/80 font-body text-lg">Connecting and Reconnecting to your body's deeper wisdom for healing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      <section className="bg-white border-b border-stone-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-stone-500 font-body text-sm">Your Progress</span>
              <div className="w-48 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-terracotta rounded-full transition-all duration-500" 
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-stone-500 font-body text-sm">{completedCount} / {programLessons.length} completed</span>
            </div>
            <div className="flex items-center gap-2 text-emerald-600">
              <Unlock size={18} />
              <span className="font-body text-sm font-medium">
                {hasRemedyAccess ? 'Full Access' : 'Intro Open - Remedies Locked'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar - Lesson List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-36">
                <div className="bg-forest p-4">
                  <h2 className="font-heading text-xl text-white">Program Content</h2>
                  <p className="text-cream/70 font-body text-sm">21 lessons - access any time</p>
                </div>
                
                <div className="max-h-[60vh] overflow-y-auto">
                  {sections.map((section) => (
                    <div key={section.id} className="border-b border-stone-100 last:border-0">
                      <button
                        onClick={() => setExpandedSection(expandedSection === section.id ? 'all' : section.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                      >
                        <span className="font-body font-medium text-forest">{section.title}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-400">{section.lessons.length}</span>
                          <ChevronDown
                            size={18}
                            className={`text-stone-400 transition-transform ${
                              expandedSection === section.id || expandedSection === 'all' ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {(expandedSection === section.id || expandedSection === 'all') && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            {section.lessons.map((lesson) => {
                              const isLocked = lesson.category === 'remedies' && !hasRemedyAccess;
                              return (
                              <button
                                key={lesson.id}
                                onClick={() => handleLessonClick(lesson)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-terracotta/5 transition-colors ${
                                  selectedLesson?.id === lesson.id ? 'bg-terracotta/10 border-l-4 border-terracotta' : ''
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isLocked
                                    ? 'bg-stone-100 text-stone-300'
                                    : completedLessons.includes(lesson.id) 
                                      ? 'bg-emerald-100 text-emerald-600' 
                                      : 'bg-stone-100 text-stone-400'
                                }`}>
                                  {isLocked ? (
                                    <Lock size={14} />
                                  ) : completedLessons.includes(lesson.id) ? (
                                    <CheckCircle size={16} />
                                  ) : (
                                    <span className="text-xs font-body font-medium">{lesson.id}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-body text-sm truncate ${isLocked ? 'text-stone-400' : 'text-forest'}`}>{lesson.title}</p>
                                  {lesson.subtitle && (
                                    <p className="font-body text-xs text-stone-400 truncate">{lesson.subtitle}</p>
                                  )}
                                </div>
                                {isLocked && <Lock size={14} className="text-stone-300 flex-shrink-0" />}
                                {!isLocked && lesson.videoUrl && <Video size={14} className="text-terracotta flex-shrink-0" />}
                              </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {selectedLesson?.locked ? (
                <motion.div
                  key="locked"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="p-8 md:p-12 text-center">
                    <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Lock size={32} className="text-stone-400" />
                    </div>
                    <h3 className="font-heading text-2xl text-forest mb-3">{selectedLesson.title}</h3>
                    <p className="text-stone-500 font-body mb-2">{selectedLesson.subtitle}</p>
                    <p className="text-stone-400 font-body text-sm mb-8 max-w-md mx-auto">
                      The remedies are available with program access. The Introduction, How to Use This Resource, Your Back Story, and The Tests are free to explore.
                    </p>
                    <Link
                      to="/contact"
                      className="bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors inline-block"
                    >
                      Contact Jill for Access
                    </Link>
                  </div>
                </motion.div>
              ) : selectedLesson ? (
                <motion.div
                  key={selectedLesson.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  {/* Video area - only show for lessons with videos or no rich content */}
                  {!selectedLesson.hasRichContent && (
                    <div className="aspect-video bg-stone-200 relative">
                      {selectedLesson.videoUrl ? (
                        <iframe
                          src={selectedLesson.videoUrl}
                          title={selectedLesson.title}
                          className="w-full h-full"
                          allowFullScreen
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-forest/10">
                          <div className="text-center">
                            <div className="w-20 h-20 bg-forest/20 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Play size={32} className="text-forest ml-1" />
                            </div>
                            <p className="text-stone-500 font-body">Video coming soon</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Lesson Info */}
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <span className="text-terracotta font-body text-sm uppercase tracking-wide">
                          Lesson {selectedLesson.id}
                        </span>
                        <h2 className="font-heading text-2xl md:text-3xl text-forest mt-1">
                          {selectedLesson.title}
                        </h2>
                        {selectedLesson.subtitle && (
                          <p className="text-stone-500 font-body text-lg mt-1">{selectedLesson.subtitle}</p>
                        )}
                      </div>
                      <button
                        onClick={() => toggleComplete(selectedLesson.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                          completedLessons.includes(selectedLesson.id)
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        <CheckCircle size={16} />
                        {completedLessons.includes(selectedLesson.id) ? 'Completed' : 'Mark Complete'}
                      </button>
                    </div>

                    {/* LESSON 1: Introduction - Multifidus & QL */}
                    {selectedLesson.id === 1 && (
                      <div>
                        <div className="text-center mb-8">
                          <h3 className="font-heading text-2xl text-forest mb-2">Understanding Multifidus & QL Muscle Timing</h3>
                        </div>

                        {/* Anatomy Images */}
                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                          <div className="bg-stone-50 rounded-2xl overflow-hidden border border-stone-200">
                            <img
                              src="https://customer-assets.emergentagent.com/job_aae2d4b5-638d-4708-af13-2bdf30ef2576/artifacts/x1fy2ls0_image.png"
                              alt="Multifidus muscle along the spine"
                              className="w-full h-64 object-contain bg-sky-50"
                            />
                            <div className="p-4 text-center bg-forest">
                              <p className="font-heading text-base text-cream">Multifidus</p>
                              <p className="font-body text-xs text-cream/70 mt-1">Deep spinal stabilizer running along the vertebrae</p>
                            </div>
                          </div>
                          <div className="bg-stone-50 rounded-2xl overflow-hidden border border-stone-200">
                            <img
                              src="https://customer-assets.emergentagent.com/job_aae2d4b5-638d-4708-af13-2bdf30ef2576/artifacts/vi5co4br_image.png"
                              alt="Quadratus Lumborum muscle"
                              className="w-full h-64 object-contain bg-stone-100"
                            />
                            <div className="p-4 text-center bg-forest">
                              <p className="font-heading text-base text-cream">Quadratus Lumborum (QL)</p>
                              <p className="font-body text-xs text-cream/70 mt-1">Deep lateral stabilizer connecting ribs to pelvis</p>
                            </div>
                          </div>
                        </div>

                        {/* Main intro text */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            If you're reading this, chances are you're experiencing some form of back discomfort — whether it's in the lower back, full spine, neck, knees, shoulders, or even the abdomen. Sometimes, this pain appears without an identifiable injury, often due to misalignments in the body and compensations.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            These misalignments can also influence mood and motivation in ways that feel unexplained. If you feel relief after using these remedies, it's a sign that your muscles are returning to proper timing.
                          </p>
                          <p className="text-forest font-body font-medium italic text-center">
                            When these muscles are out of timing we tend to feel a little discombobulated and our back feels at risk for injury or spasm.
                          </p>
                        </div>

                        {/* Understanding This Level of the Body */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-cream">Understanding This Level of the Body</h4>
                          </div>
                          <div className="p-6">
                            <div className="text-center mb-6">
                              <p className="text-stone-600 font-body leading-relaxed">Most back pain does not come from damage.</p>
                              <p className="font-heading text-2xl text-terracotta mt-1">It comes from timing.</p>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-5 mb-6">
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                This work focuses on two deep stabilizing muscles in the lower back: the <strong>Multifidus</strong> and the <strong>Quadratus Lumborum (QL)</strong>.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                These muscles do not create big movement. They create <strong>organization, stability and postural integrity</strong>.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                When they are working well, the spine feels supported, responsive, and safe. When they are out of timing, the body often compensates in ways that lead to pain, stiffness, instability, or a sense that the back is "at risk," even without a clear injury.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Three Levels */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">The Body's 3-Level Stability System</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <div className="rounded-xl overflow-hidden border border-stone-200 mb-4">
                              <img
                                src="https://customer-assets.emergentagent.com/job_aae2d4b5-638d-4708-af13-2bdf30ef2576/artifacts/dib8vsqy_image.png"
                                alt="The body's 3 muscle levels - intrinsic, inner unit, and outer unit"
                                className="w-full object-contain"
                              />
                            </div>
                            <div className="bg-forest rounded-xl p-5 text-cream">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="bg-cream text-forest w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">1</span>
                                <p className="font-heading text-lg">Intrinsic Muscles</p>
                              </div>
                              <p className="font-body text-sm text-cream/90 leading-relaxed">
                                Small muscles attaching closest to the skeleton — especially spine, pelvis, and base of skull. These muscles are more instinct-oriented and preparatory, protecting the spine. They respond to intention, eye movement, and breath. We don't have conscious control over these muscles.
                              </p>
                            </div>

                            <div className="bg-forest/80 rounded-xl p-5 text-cream">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="bg-cream text-forest w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">2</span>
                                <p className="font-heading text-lg">Inner Unit</p>
                              </div>
                              <p className="font-body text-sm text-cream/90 leading-relaxed mb-2">
                                Transverse Abdominis, hamstrings, psoas, Quadratus Lumborum, Multifidus, suboccipitals, etc. Level 2 has both intrinsic and conscious aspects.
                              </p>
                              <p className="font-body text-sm text-cream/90 leading-relaxed">
                                These muscles become sluggish and lazy from a lifetime of sitting, which is why we begin to have stooped posture and experience a lot of stiffness and pain as this level becomes desensitized.
                              </p>
                            </div>

                            <div className="bg-forest/60 rounded-xl p-5 text-cream">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="bg-cream text-forest w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">3</span>
                                <p className="font-heading text-lg">Outer Unit</p>
                              </div>
                              <p className="font-body text-sm text-cream/90 leading-relaxed mb-2">
                                The level you can see, feel, and that gives the body its shape. These muscles are meant for movement, acceleration, deceleration, and protection. Easily activated consciously.
                              </p>
                              <p className="font-body text-sm text-cream/90 leading-relaxed">
                                This level can become very stiff — mainly because we end up asking it to do posture for us, when it's designed to <em>move</em> us. Our movement muscles begin to believe their job is to hold us still, doing double duty.
                              </p>
                            </div>

                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-4">
                              <p className="text-forest font-body text-sm leading-relaxed">
                                When these levels are communicating well and firing in sequence, we feel and move at ease, trusting the body to move in a functional way.
                              </p>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                              <p className="text-stone-700 font-body text-sm leading-relaxed">
                                When these levels are not awake and not firing in proper sequence, it can cause significant pain all over — especially in the low back, neck, shoulders, hips, knees, or even the abdomen — without knowing why. Sometimes this also shows up as unexplained fatigue, low motivation, or a general sense of being unsettled in the body. These can all be signs of misfiring or poor sequencing in the stabilizing system.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Signs of Recovery */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">You'll Know These Muscles Are Coming Back Into Timing When:</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {[
                                'Movement feels easier',
                                'Pain reduces',
                                'Your body feels more coordinated',
                                'A general sense of relief or "settling"',
                              ].map((sign, idx) => (
                                <div key={idx} className="flex gap-2 items-center bg-emerald-50 rounded-lg p-3">
                                  <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                                  <p className="text-forest font-body text-sm">{sign}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Why These Muscles Go Offline */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-stone-100 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Why These Muscles Go Offline</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                              Misfiring or poor sequencing can develop from many things, including:
                            </p>
                            <div className="grid sm:grid-cols-2 gap-2 mb-6">
                              {[
                                'Surgery',
                                'Long-standing pain',
                                'Old or repeated injuries',
                                'Prolonged sitting or travel (especially flights)',
                                'Habitual postures',
                                'Leg crossing or favoring one side',
                                'Handedness and daily movement preferences',
                              ].map((cause, idx) => (
                                <div key={idx} className="flex gap-2 items-start">
                                  <span className="w-1.5 h-1.5 bg-stone-400 rounded-full mt-1.5 flex-shrink-0" />
                                  <p className="text-stone-600 font-body text-sm">{cause}</p>
                                </div>
                              ))}
                            </div>

                            <div className="bg-terracotta/10 rounded-xl p-5 text-center">
                              <p className="font-heading text-lg text-forest mb-2">This is not simply aging.</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed mb-3">
                                What we often call aging is the gradual accumulation of compensations. The body learns to rely on preferred patterns and slowly loses balance between left and right, front and back, stability and movement.
                              </p>
                              <p className="text-forest font-body font-medium italic">
                                The good news is: this system is highly responsive to attention, awareness, and gentle re-patterning.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Program overview */}
                        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mb-6">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Program Flow</h4>
                          </div>
                          <div className="p-6">
                            <div className="flex flex-col gap-3">
                              {[
                                { step: '1', label: 'Introduction', desc: 'Understand the key muscles (you are here)' },
                                { step: '2', label: 'Tests', desc: 'Assess your Multifidus & QL timing' },
                                { step: '3', label: 'Flow', desc: 'Follow the flow chart based on your results' },
                                { step: '4', label: '16 Remedies', desc: 'Targeted practices to restore balance' },
                              ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4">
                                  <span className="bg-terracotta text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">{item.step}</span>
                                  <div className="flex-1">
                                    <p className="font-body font-medium text-forest text-sm">{item.label}</p>
                                    <p className="font-body text-xs text-stone-500">{item.desc}</p>
                                  </div>
                                  {idx < 3 && <ChevronDown size={16} className="text-stone-300 rotate-0 md:-rotate-90" />}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          <button
                            onClick={() => setSelectedLesson(programLessons[1])}
                            className="bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors inline-flex items-center gap-2"
                          >
                            How to Use This Resource <ChevronDown size={18} className="-rotate-90" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* LESSON 2: How to Use This Resource */}
                    {selectedLesson.id === 2 && (
                      <div>
                        {/* Start Small */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <h4 className="font-heading text-2xl text-forest mb-4">Start small.</h4>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Begin with The Tests always and the first 4 remedies. Different symptoms in the Tests will point you further.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            Since the tests often <em>are</em> the remedy a lot of the time, we always begin there.
                          </p>
                        </div>

                        {/* The Real Magic */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Where the Magic Really Lies</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              The magic lies less in the actual remedy and more in <strong>connecting to the body</strong> — and our brain witnessing what IS, and maybe more importantly, what ISN'T happening.
                            </p>
                            <div className="bg-forest/5 rounded-xl p-5 text-center">
                              <p className="font-heading text-xl text-forest">
                                The Brain-Body Connection is a very powerful wiring system.
                              </p>
                              <p className="text-stone-500 font-body text-sm mt-2 italic">It's truly the master system.</p>
                            </div>
                          </div>
                        </div>

                        {/* The Faulty Blueprint */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Faulty Blueprint</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              What's happening in large part is you likely have a faulty blueprint in walking. Maybe you stubbed your toe once, or sprained an ankle or a knee — pain is a high motivator for change. And we "change" our walk, weight distribution, joint positions, etc. due to the pain.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed mb-6">
                              When we aren't in pain anymore, we tend to revert out of the pain blueprint (limp) and <em>appear</em> to return to a balanced gait. But we rarely return to a true neutral.
                            </p>

                            {/* The Compensation Chain */}
                            <div className="flex flex-wrap items-center justify-center gap-2 mb-6 py-4">
                              {['Injury', 'Compensation', 'Habit', 'Structural Change', 'New Baseline'].map((step, idx) => (
                                <React.Fragment key={idx}>
                                  <span className="bg-terracotta text-cream px-4 py-2 rounded-full font-body text-sm font-medium">{step}</span>
                                  {idx < 4 && <ChevronDown size={16} className="text-stone-300 -rotate-90" />}
                                </React.Fragment>
                              ))}
                            </div>

                            <div className="bg-forest/5 rounded-xl p-5 mb-4">
                              <p className="text-stone-600 font-body leading-relaxed">
                                An ideal, healthy or functional baseline would be for our limbs to be within <strong>10%</strong> of each other. For illustration purposes — we are very tolerant to approximately a 60/40 differential, and the minute we hit 39/61, we are in pain.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Compensation & What We Call "Aging" */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-stone-100 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">The Genius of Compensation</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              Our bodies are amazing at compensation. It's a survival technique and truly a thing of magic. It's also to our detriment. What we typically call "aging" is really <strong>layered compensations</strong> from many years of:
                            </p>
                            <div className="grid sm:grid-cols-2 gap-2 mb-6">
                              {[
                                'Dominant handedness',
                                'Postural, seated, & sleeping preferences',
                                'Injuries',
                                'Surgeries & scar tissue',
                                'Car accidents & other impacts',
                                'A host of other reasons',
                              ].map((cause, idx) => (
                                <div key={idx} className="flex gap-2 items-start">
                                  <span className="w-1.5 h-1.5 bg-terracotta rounded-full mt-1.5 flex-shrink-0" />
                                  <p className="text-stone-600 font-body text-sm">{cause}</p>
                                </div>
                              ))}
                            </div>

                            <div className="bg-terracotta/10 rounded-xl p-6 text-center">
                              <p className="font-heading text-2xl text-forest mb-2">What we call AGE is really</p>
                              <p className="font-heading text-2xl text-terracotta">LAYERED FAULTY BLUEPRINTS</p>
                            </div>
                          </div>
                        </div>

                        {/* The Solution */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-emerald-200 overflow-hidden">
                          <div className="bg-emerald-500 px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Creating a New Blueprint</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              It can be very important to create a newer, more functional blueprint and pattern. In order to do so, we must <strong>slow right down</strong> and connect to the beautiful aspect of our body's healing system — where the brain sees the problem, diagnoses, and re-patterns over time.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              You just need to slow down enough and explore small areas of biomechanics in order for the brain to be able to <strong>witness, highlight, and get to work on re-patterning</strong>.
                            </p>
                          </div>
                        </div>

                        {/* Practical Guidance */}
                        <div className="mb-8 bg-forest/5 rounded-2xl p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            So start with Remedy 1 and progress only as needed. Many people find that the first few steps are enough most of the time.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Some bodies are more complex. If a remedy doesn't work on a given day, it doesn't mean you're doing it wrong. It simply means a different entry point may be needed. Begin to explore further.
                          </p>
                        </div>

                        {/* Worked Together / Haven't */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="p-6 space-y-4">
                            <div className="bg-terracotta/10 rounded-xl p-4">
                              <p className="text-stone-600 font-body leading-relaxed">
                                If we've worked together before, use this as a reminder of tools you already know.
                              </p>
                            </div>
                            <div className="bg-terracotta/10 rounded-xl p-4">
                              <p className="text-stone-600 font-body leading-relaxed">
                                If we haven't, this guide can still help you explore your body and restore some degree of sequencing and comfort. <strong>Book a discovery session</strong> for an assessment and further guidance to work through your specific blueprint.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8">
                          <p className="text-forest font-body leading-relaxed mb-2">
                            Videos will be available to support this work.
                          </p>
                          <p className="text-forest font-body leading-relaxed">
                            You're also welcome to reach out for guidance, in person or online.
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-3 justify-center">
                          <button
                            onClick={() => setSelectedLesson(programLessons[2])}
                            className="bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors inline-flex items-center gap-2"
                          >
                            Your Back Story <ChevronDown size={18} className="-rotate-90" />
                          </button>
                          <Link
                            to="/contact"
                            className="bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
                          >
                            Book a Discovery Session
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* LESSON 3 → now id 4: The Tests */}
                    {selectedLesson.id === 4 && (
                      <div>
                        {/* Multifidus Test */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Multifidus Test</h4>
                            <p className="text-cream/80 font-body text-sm">Leg / Head Lift Test</p>
                          </div>
                          <div className="p-6 space-y-5">
                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="font-body font-medium text-forest text-sm mb-2">Leg Lift</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Lying face down, use hands for a pillow under forehead. Gently lift both legs to fire muscles in the low back, then gently lift right then left — may do a few times.
                              </p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed mt-2">
                                If someone is helping, get them to dig fingers into low back muscles very firmly, to help you feel the muscles fire, not fire, or misfire. They can likely feel the imbalances and give you feedback — e.g. one side of multifidus is dormant, or the other side may be in spasm doing too much work.
                              </p>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="font-body font-medium text-forest text-sm mb-2">Head Lift — Basic</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Lift the head naturally and see if the head feels connected to lower back muscles.
                              </p>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="font-body font-medium text-forest text-sm mb-2">Head Lift — With Rotation</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Lift head as if from sternum or even from these low back muscles and rotate left, put ear down. Pick up head the same way, and rotate all the way over and put right ear down. Repeat until muscles are retimed and low back feels more comfortable.
                              </p>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="font-body font-medium text-forest text-sm mb-2">Head Sweeping</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                May need to add head sweeping — do as above but sweep head almost out towards elbow of each side. You will usually feel a hip hike as a helper (head going right, right hip hikes). May not be happening on one side — may have to ask it to wake up.
                              </p>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="font-body font-medium text-forest text-sm mb-2">Quadrant Lifting</p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                May need a bit more — quadrant lifting. Place left ear down looking at right elbow. Lift entire right upper quadrant of body: lift head and keep hand attached to forehead, lifting head, neck, arm, shoulder and chest of right side as a unit. Repeat left. Do several until dormant multifidus wakes up and is doing roughly equal work.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* QL Test */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-forest/30 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Quadratus Lumborum Test</h4>
                          </div>
                          <div className="p-6">
                            <div className="bg-forest/5 rounded-xl p-4 mb-4">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Lift head much as described above but rotate head and reach forehead for the crook of the elbow (Jill calls it "smelling your armpit" for levity).
                              </p>
                            </div>
                            <div className="bg-forest/5 rounded-xl p-4 mb-4">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Repeat both sides — both stopping in centre forehead down and restarting, and also all the way from one side to the other. The spine will C-shape to help your forehead arrive.
                              </p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                              <p className="text-amber-800 font-body text-sm leading-relaxed font-medium">
                                Notice if you have neck pain or limitations going to either side. If yes, need to proceed to{' '}
                                <a
                                  href="https://youtu.be/ePjCDq03Zqg"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline decoration-2 underline-offset-2 text-terracotta hover:text-terracotta-dark font-semibold transition-colors"
                                  data-testid="hanna-3-part-flexion-link"
                                >
                                  Hanna 3-Part Flexion
                                </a>.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* What's Next */}
                        <div className="bg-terracotta/10 rounded-2xl p-6 text-center">
                          <h4 className="font-heading text-xl text-forest mb-2">What's Next?</h4>
                          <p className="text-stone-600 font-body text-sm">Based on your test results, follow the flow chart to identify which of the <strong>17 remedies</strong> will best address your specific imbalances.</p>
                        </div>
                      </div>
                    )}

                    {/* Generic content for other lessons */}
                    {!selectedLesson.hasRichContent && (
                      <div className="prose prose-stone max-w-none">
                        <p className="text-stone-600 font-body leading-relaxed">
                          {selectedLesson.description}
                        </p>
                        <div className="mt-8 p-6 bg-cream rounded-xl">
                          <h3 className="font-heading text-lg text-forest mb-2">Lesson Content</h3>
                          <p className="text-stone-500 font-body text-sm">
                            Full lesson content, exercises, and resources will be added here.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                /* Welcome/Overview Screen */
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-3xl text-forest mb-4">Welcome to The Back Reset Program</h2>
                    <p className="text-stone-600 font-body text-lg max-w-2xl mx-auto">
                      This program is designed for you to explore at your own pace. Jump to any lesson 
                      based on what your back needs today. There's no required order — trust your body 
                      to guide you.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-cream rounded-xl p-6">
                      <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center mb-4">
                        <Unlock size={24} className="text-terracotta" />
                      </div>
                      <h3 className="font-heading text-lg text-forest mb-2">Open Access</h3>
                      <p className="text-stone-600 font-body text-sm">
                        All 21 lessons are available to you immediately. No need to complete them in order.
                      </p>
                    </div>
                    <div className="bg-cream rounded-xl p-6">
                      <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center mb-4">
                        <Clock size={24} className="text-terracotta" />
                      </div>
                      <h3 className="font-heading text-lg text-forest mb-2">Your Pace</h3>
                      <p className="text-stone-600 font-body text-sm">
                        Return to lessons whenever you need them. This is a resource you'll use again and again.
                      </p>
                    </div>
                  </div>

                  <div className="bg-forest/5 rounded-xl p-6">
                    <h3 className="font-heading text-lg text-forest mb-4">Suggested Starting Points</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <button
                        onClick={() => setSelectedLesson(programLessons[0])}
                        className="text-left p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                      >
                        <span className="text-terracotta font-body text-xs uppercase">New Here?</span>
                        <p className="font-body font-medium text-forest">Start with the Introduction</p>
                      </button>
                      <button
                        onClick={() => setSelectedLesson(programLessons[3])}
                        className="text-left p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                      >
                        <span className="text-terracotta font-body text-xs uppercase">Assess Yourself</span>
                        <p className="font-body font-medium text-forest">The Tests</p>
                      </button>
                      <button
                        onClick={() => setSelectedLesson(programLessons[4])}
                        className="text-left p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                      >
                        <span className="text-terracotta font-body text-xs uppercase">Jump In</span>
                        <p className="font-body font-medium text-forest">Remedy 1: The Test is the Remedy</p>
                      </button>
                    </div>
                  </div>

                  <p className="text-center text-stone-400 font-body text-sm mt-8">
                    Select a lesson from the sidebar to begin
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BackResetProgramPage;
